/**
 * Orthodox Pascha calculation.
 *
 * Implements Meeus's Julian-calendar Easter algorithm (the "Anonymous Julian"
 * / Gauss-style formula) which gives the Julian-calendar date of Pascha for
 * any year ≥ 326. The Julian date is then converted to Gregorian using the
 * standard Julian/Gregorian offset for the year.
 *
 * Validated against published Orthodox Paschalion tables, see
 * tests/pascha.test.ts for 50+ cross-referenced fixtures.
 *
 *   References:
 *   - Meeus, Astronomical Algorithms, ch. 8
 *   - The Russian Orthodox Paschalion (Russian Orthodox Church Outside Russia)
 *   - The "Date of Easter" tables published by GOARCH and OCA
 *
 * The algorithm:
 *
 *   a = year mod 4
 *   b = year mod 7
 *   c = year mod 19
 *   d = (19c + 15) mod 30
 *   e = (2a + 4b - d + 34) mod 7
 *   month_julian = (d + e + 114) // 31      (3 = March, 4 = April)
 *   day_julian   = ((d + e + 114) mod 31) + 1
 */
import { julianGregorianOffset, addDays } from './dates.js';
export function paschaJulian(year) {
    if (!Number.isInteger(year) || year < 326) {
        throw new RangeError(`Pascha is only defined from year 326 onwards (got ${year}).`);
    }
    const a = year % 4;
    const b = year % 7;
    const c = year % 19;
    const d = (19 * c + 15) % 30;
    const e = (2 * a + 4 * b - d + 34) % 7;
    // Modulo can be negative in JavaScript when the dividend is, guard against it.
    const eNormalized = ((e % 7) + 7) % 7;
    const total = d + eNormalized + 114;
    const month = Math.floor(total / 31);
    const day = (total % 31) + 1;
    return { month, day };
}
/**
 * Orthodox Pascha as a Gregorian-calendar date. This is the date most
 * Western-calendar users see on their wall calendar.
 */
export function paschaGregorian(year) {
    const { month, day } = paschaJulian(year);
    // The Julian date is in the same calendar year as the result, so we apply
    // the offset based on `year` and let addDays carry into May where needed.
    const offset = julianGregorianOffset(year);
    return addDays({ year, month, day }, offset);
}
//# sourceMappingURL=pascha.js.map